import { InjectionToken } from '@angular/core';

/**
 * Identity and build values stamped on every telemetry item by {@link ApplicationInsightsService}, following the
 * client telemetry attribute baseline (role, service namespace/name/version, environment). Each shell supplies its
 * own so the shared service carries no app names; shells that do not provide it keep the legacy behaviour of using
 * {@link CLIENT_TYPE_TOKEN} as the cloud role and no baseline attributes.
 */
export interface TelemetryConfig {
    /** The `ai.cloud.role` tag, e.g. 'marlin-mobile.app'. */
    roleName: string;
    /** The `service.namespace` property, e.g. 'marlin'. */
    serviceNamespace: string;
    /** The `service.name` property, e.g. 'mobile'. */
    serviceName: string;
    /**
     * Resolves the app version, sent as `ai.application.ver` and `service.version`. Asynchronous because a native shell
     * reads it from the installed package (stamped from the release tag at build time); it is resolved once when
     * telemetry starts. Without one, or when it fails, the version is 'local'.
     */
    resolveVersion?: () => Promise<string>;
    /** The deployment environment (development, staging, production, or local), sent as `deployment.environment.name`. */
    environment: string;
    /**
     * Absolute API base URLs whose hosts receive W3C trace headers (traceparent, tracestate), so a call joins up
     * with the server request it caused. Only our own APIs belong here; every other host gets no trace headers.
     */
    correlationUrls: string[];
}

/** Optional per-shell telemetry identity and build values. See {@link TelemetryConfig}. */
export const TELEMETRY_CONFIG = new InjectionToken<TelemetryConfig>('TELEMETRY_CONFIG');
